import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, EyeOff, Users, Image as ImageIcon, Heart, Smartphone, Lock, Folder } from 'lucide-react';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import LanguageSwitcher from '../components/LanguageSwitcher';

const demoMediaItems = [
  { id: '1', kind: 'image' as const, mime: 'image/jpeg', dataUrl: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80', width: 800, height: 600 },
  { id: '2', kind: 'image' as const, mime: 'image/jpeg', dataUrl: 'https://images.unsplash.com/photo-1506953823976-52e1fdc0149a?w=800&q=80', width: 800, height: 600 },
  { id: '3', kind: 'image' as const, mime: 'image/jpeg', dataUrl: 'https://images.unsplash.com/photo-1473116763249-2faaef81ccda?w=800&q=80', width: 800, height: 600 },
];

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const localizedPath = useLocalizedPath();

  // Scroll listener for the "How it Works" section
  useEffect(() => {
    const handleScroll = () => {
      const steps = ['step-0', 'step-1', 'step-2'];
      let currentStep = 0;

      for (let i = steps.length - 1; i >= 0; i--) {
        const element = document.getElementById(steps[i]);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Adjust threshold - when the element crosses the vertical center of the viewport
          if (rect.top <= window.innerHeight / 2) {
            currentStep = i;
            break;
          }
        }
      }
      setActiveStep(currentStep);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-clip selection:bg-waffle-orange/30">
      {/* Dynamic Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-waffle-orange/20 rounded-full mix-blend-screen filter blur-[100px] opacity-50 animate-blob-slow" />
        <div className="absolute top-[20%] right-[-10%] w-[30%] h-[50%] bg-amber-500/10 rounded-full mix-blend-screen filter blur-[100px] opacity-50 animate-blob-slow animation-delay-2000" />
        <div className="absolute bottom-[-10%] left-[20%] w-[40%] h-[40%] bg-orange-600/15 rounded-full mix-blend-screen filter blur-[100px] opacity-50 animate-blob-slow animation-delay-4000" />
        <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.03] mix-blend-overlay"></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-gray-950/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href={localizedPath('/')} className="flex items-center gap-3 group">
              <img src="/logo.svg" alt="Family Waffle" className="w-10 h-10 group-hover:scale-105 transition-transform duration-300" />
              <span className="text-xl font-bold tracking-tight">Family Waffle</span>
            </a>

            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">{tc('header.features')}</a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">{tc('header.howItWorks')}</a>
              <LanguageSwitcher />
              <a href="#download" className="px-5 py-2.5 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-all hover:scale-105 active:scale-95">
                {tc('header.downloadApp')}
              </a>
            </nav>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-white">
              <div className={`space-y-1.5 transition-all ${mobileMenuOpen ? 'rotate-90' : ''}`}>
                <span className="block w-6 h-0.5 bg-current"></span>
                <span className="block w-6 h-0.5 bg-current"></span>
                <span className="block w-4 h-0.5 bg-current"></span>
              </div>
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 flex flex-col gap-4 items-start">
              <a href="#features" className="text-sm font-medium text-gray-300 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>{tc('header.features')}</a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-300 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>{tc('header.howItWorks')}</a>
              <a href="#download" className="px-5 py-2.5 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-all" onClick={() => setMobileMenuOpen(false)}>
                {tc('header.downloadApp')}
              </a>
              <LanguageSwitcher />
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10 text-center lg:text-left">
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
                {t('hero.headline1')}<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-waffle-orange to-amber-400">
                  {t('hero.headline2')}
                </span>
              </h1>

              <p className="text-xl text-gray-400 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t('hero.subheadline')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#download" className="group px-8 py-4 bg-waffle-orange hover:bg-orange-500 text-white font-semibold rounded-full transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_-10px_rgba(249,115,22,0.5)] flex items-center justify-center gap-2">
                  {t('hero.ctaPrimary')}
                  <Heart className="w-5 h-5 group-hover:block hidden" />
                </a>
                <a href="#features" className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold rounded-full backdrop-blur-md transition-all">
                  {t('hero.ctaSecondary')}
                </a>
              </div>
            </div>

            {/* Premium Glass Mockup */}
            <div className="relative mx-auto w-full max-w-[320px] lg:max-w-[400px] perspective-[1000px] hidden md:block">
              <div className="relative transform-gpu hover:rotate-y-[-5deg] hover:rotate-x-[5deg] transition-transform duration-700 ease-out z-20">
                <div className="rounded-[40px] p-2 bg-gradient-to-br from-white/20 to-white/0 border border-white/10 backdrop-blur-3xl shadow-2xl relative">
                  <div className="absolute inset-0 bg-gradient-to-tr from-waffle-orange/20 to-transparent rounded-[40px] opacity-50 blur-xl -z-10" />
                  <div className="rounded-[32px] overflow-hidden bg-gray-950 border border-white/10 relative">
                    <div className="absolute top-0 inset-x-0 h-8 bg-black/50 backdrop-blur-md z-10 flex justify-center items-end pb-2">
                      <div className="w-24 h-5 bg-black rounded-b-xl" />
                    </div>

                    <div className="p-4 pt-12 space-y-4">
                      {/* Family Header */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-waffle-orange to-amber-500 flex items-center justify-center font-bold">S</div>
                        <div>
                          <p className="font-semibold text-sm">{t('mockup.sarah')}</p>
                          <p className="text-xs text-gray-400">{t('mockup.justNow')}</p>
                        </div>
                      </div>

                      {/* Photo Grid */}
                      <div className="rounded-2xl overflow-hidden border border-white/5">
                        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80" className="w-full h-48 object-cover" alt="Memory" />
                      </div>

                      {/* Caption */}
                      <div className="p-3 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-md">
                        <p className="text-sm text-gray-300">{t('mockup.caption')}</p>
                      </div>

                      {/* Security Badge */}
                      <div className="flex items-center justify-center gap-2 pt-2 pb-1">
                        <Lock className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-xs font-medium text-gray-500">{t('mockup.encrypted')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}

            </div>
          </div>
        </section>

        {/* Bento Grid Features */}
        <section id="features" className="py-24 px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-4">
              {t('features.heading1')}<br />
              <span className="text-gray-500">{t('features.heading2')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Double width card */}
            <div className="md:col-span-2 group relative rounded-[2rem] p-8 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden hover:border-waffle-orange/50 transition-all duration-500 backdrop-blur-md">
              <div className="absolute inset-0 bg-gradient-to-b from-waffle-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-waffle-orange/20 to-amber-500/10 text-waffle-orange rounded-3xl flex items-center justify-center shadow-inner border border-waffle-orange/20 group-hover:scale-110 transition-transform duration-500">
                  <Shield className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2">{t('features.privacy.title')}</h3>
                  <p className="text-gray-400">{t('features.privacy.description')}</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-[2rem] p-8 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden hover:border-amber-500/50 transition-all duration-500 backdrop-blur-md">
              <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500/20 to-orange-500/10 text-amber-500 rounded-3xl flex items-center justify-center shadow-inner border border-amber-500/20 group-hover:scale-110 transition-transform duration-500">
                  <EyeOff className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2">{t('features.noAlgorithms.title')}</h3>
                  <p className="text-gray-400">{t('features.noAlgorithms.description')}</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-[2rem] p-8 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden hover:border-orange-500/50 transition-all duration-500 backdrop-blur-md">
              <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-600/20 to-red-500/10 text-orange-500 rounded-3xl flex items-center justify-center shadow-inner border border-orange-500/20 group-hover:scale-110 transition-transform duration-500">
                  <Folder className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2">{t('features.saveStorage.title')}</h3>
                  <p className="text-gray-400">{t('features.saveStorage.description')}</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 lg:col-span-4 group relative rounded-[2rem] p-8 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden hover:border-waffle-orange/50 transition-all duration-500 backdrop-blur-md">
              <div className="absolute inset-0 bg-gradient-to-b from-waffle-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-waffle-orange/20 to-amber-500/10 text-waffle-orange rounded-3xl flex items-center justify-center shadow-inner border border-waffle-orange/20 group-hover:scale-110 transition-transform duration-500">
                  <ImageIcon className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-2">{t('features.originalQuality.title')}</h3>
                  <p className="text-gray-400">{t('features.originalQuality.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Built for People Who Don't Post */}
        <section className="py-24 px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="relative rounded-[3rem] p-10 lg:p-16 bg-gradient-to-br from-white/5 to-transparent border border-white/10 backdrop-blur-md text-center overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-waffle-orange/10 rounded-full blur-3xl opacity-50" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl opacity-50" />

            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-10 relative z-10">
              {t('philosophy.heading1')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-waffle-orange to-amber-400">{t('philosophy.heading2')}</span>
            </h2>

            <div className="space-y-6 text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto relative z-10 font-medium">
              <p>{t('philosophy.p1')}</p>
              <p>{t('philosophy.p2')}<span className="text-gray-200">{t('philosophy.p2highlight')}</span></p>
              <p>{t('philosophy.p3')}</p>
              <p className="text-2xl text-gray-300 pt-4">{t('philosophy.p4')}</p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(['t1', 't2', 't3'] as const).map((key) => (
              <div key={key} className="p-8 rounded-[2rem] bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 backdrop-blur-md">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-waffle-orange" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "{t(`testimonials.${key}.quote`)}"
                </p>
                <span className="text-sm text-gray-500">{t(`testimonials.${key}.author`)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* How it Works / Steps - Award Winning Redesign */}
        <section id="how-it-works" className="py-24 lg:py-40 px-6 lg:px-8 max-w-7xl mx-auto relative">
          <div className="text-center mb-16 lg:mb-32">
            <h2 className="text-4xl lg:text-6xl font-bold tracking-tight mb-6">
              {t('howItWorks.heading')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('howItWorks.subheading')}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 relative lg:items-start">
            {/* Sticky Mobile Mockup (Desktop) / Static (Mobile) */}
            <div className="lg:w-1/2 lg:sticky lg:top-32 h-[500px] lg:h-[600px] flex items-center justify-center order-2 lg:order-1 perspective-[1000px]">
              <div className="relative w-full max-w-[320px] lg:max-w-[360px] transform-gpu transition-transform duration-700 ease-out z-20 mx-auto">
                <div className="rounded-[40px] p-2 bg-gradient-to-br from-white/20 to-white/0 border border-white/10 backdrop-blur-3xl shadow-2xl relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-tr from-waffle-orange/20 to-amber-500/20 rounded-[40px] opacity-50 blur-xl -z-10 transition-colors duration-1000" />

                  {/* Phone Screen */}
                  <div className="rounded-[32px] overflow-hidden bg-gray-950 border border-white/10 relative h-[600px] flex flex-col">
                    <div className="absolute top-0 inset-x-0 h-8 bg-black/50 backdrop-blur-md z-30 flex justify-center items-end pb-2">
                      <div className="w-24 h-5 bg-black rounded-b-xl" />
                    </div>

                    {/* Dynamic Content Based on Active Step */}
                    <div className="flex-1 relative mt-12 bg-gray-950">

                      {/* Step 1: Download UI */}
                      <div className={`absolute inset-0 p-6 flex flex-col items-center justify-center text-center transition-all duration-700 ${activeStep === 0 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'}`}>
                        <div className="w-24 h-24 bg-gradient-to-br from-waffle-orange to-amber-500 rounded-3xl mb-8 flex items-center justify-center shadow-[0_0_40px_-5px_rgba(249,115,22,0.4)]">
                          <img src="/logo.svg" alt="App Icon" className="w-12 h-12" />
                        </div>
                        <h3 className="text-2xl font-bold mb-3">{t('howItWorks.step1.phoneLabel')}</h3>
                        <p className="text-gray-400 text-sm mb-12">{t('howItWorks.step1.phoneSubLabel')}</p>
                        <div className="w-full h-12 bg-white/10 rounded-full animate-pulse mb-6 flex items-center justify-center">
                          <span className="text-white font-medium">{t('howItWorks.step1.downloading')}</span>
                        </div>
                      </div>

                      {/* Step 2: Invite UI */}
                      <div className={`absolute inset-0 p-6 flex flex-col transition-all duration-700 ${activeStep === 1 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'}`}>
                        <h3 className="text-xl font-bold mb-8 text-center">{t('howItWorks.step2.addFamily')}</h3>
                        <div className="space-y-4">
                          <div className="p-4 rounded-2xl border border-waffle-orange/50 bg-waffle-orange/10 flex items-center justify-between">
                            <span className="text-sm text-waffle-orange truncate flex-1">waffle.family/join/3f8a</span>
                            <div className="w-8 h-8 rounded-full bg-waffle-orange text-white flex items-center justify-center flex-shrink-0 ml-3">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                            </div>
                          </div>
                          <div className="text-center text-xs text-gray-500 uppercase tracking-wider my-6">{t('howItWorks.step2.orSendVia')}</div>
                          <div className="grid grid-cols-4 gap-3">
                            <div className="aspect-square rounded-xl bg-[#25D366]/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                            </div>
                            <div className="aspect-square rounded-xl bg-blue-500/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.477 2 2 6.477 2 12c0 5.522 4.477 10 10 10 5.522 0 10-4.478 10-10C22 6.477 17.522 2 12 2zm1.666 14.599l-2.03 1.15c-.246.138-.545-.038-.545-.316v-2.188c0-.751-.557-1.359-1.309-1.359H6.945c-.496 0-.91-.402-.91-.897v-6.09c0-.495.414-.897.91-.897h10.11c.496 0 .91.402.91.897v6.09c0 .495-.414.897-.91.897h-2.152a.897.897 0 0 0-.897.897v1.816z" /></svg>
                            </div>
                            <div className="aspect-square rounded-xl bg-gray-600/20 shadow-inner flex items-center justify-center"></div>
                            <div className="aspect-square rounded-xl bg-gray-600/20 shadow-inner flex items-center justify-center"></div>
                          </div>
                        </div>
                      </div>

                      {/* Step 3: Share UI */}
                      <div className={`absolute inset-0 p-4 transition-all duration-700 ${activeStep === 2 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'}`}>
                        {/* Family Header */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-waffle-orange to-amber-500 flex items-center justify-center font-bold text-sm">Y</div>
                          <div>
                            <p className="font-semibold text-sm">{t('howItWorks.step3.you')}</p>
                            <p className="text-xs text-gray-400">{t('howItWorks.step3.justNow')}</p>
                          </div>
                        </div>

                        {/* Photo Grid */}
                        <div className="rounded-2xl overflow-hidden border border-white/5 mb-4">
                          <img src={demoMediaItems[2].dataUrl} className="w-full h-48 object-cover" alt="Memory" />
                        </div>

                        {/* Engagement (Likes only) */}
                        <div className="flex items-center gap-2 mb-3 px-1">
                          <div className="flex -space-x-2">
                            <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center border border-gray-900 z-20 text-[10px] font-bold">M</div>
                            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center border border-gray-900 z-10 text-[10px] font-bold">D</div>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">{t('howItWorks.step3.lovedThis')}</span>
                        </div>

                        <div className="p-3 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-md">
                          <p className="text-sm text-gray-300">Another beautiful sunset! 🌅</p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrolling Steps Text */}
            <div className="lg:w-1/2 flex flex-col justify-center order-1 lg:order-2 lg:py-48 z-10">
              <div
                id="step-0"
                className={`transition-all duration-700 lg:min-h-[60vh] flex flex-col justify-center mb-16 lg:mb-0 ${activeStep === 0 ? 'opacity-100 scale-100' : 'opacity-40 lg:opacity-20 scale-95 lg:scale-95 blur-0 lg:blur-sm'}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-white/10 to-white/0 border border-white/10 rounded-2xl flex items-center justify-center shadow-xl backdrop-blur-md mb-6 transition-colors duration-500">
                  <Smartphone className={`w-8 h-8 ${activeStep === 0 ? 'text-waffle-orange' : 'text-gray-500'}`} />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold mb-6">{t('howItWorks.step1.title')}</h3>
                <p className="text-xl text-gray-400 leading-relaxed font-medium">
                  {t('howItWorks.step1.description')}
                </p>
              </div>

              <div
                id="step-1"
                className={`transition-all duration-700 lg:min-h-[60vh] flex flex-col justify-center mb-16 lg:mb-0 ${activeStep === 1 ? 'opacity-100 scale-100' : 'opacity-40 lg:opacity-20 scale-95 lg:scale-95 blur-0 lg:blur-sm'}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-white/10 to-white/0 border border-white/10 rounded-2xl flex items-center justify-center shadow-xl backdrop-blur-md mb-6 transition-colors duration-500">
                  <Users className={`w-8 h-8 ${activeStep === 1 ? 'text-amber-500' : 'text-gray-500'}`} />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold mb-6">{t('howItWorks.step2.title')}</h3>
                <p className="text-xl text-gray-400 leading-relaxed font-medium">
                  {t('howItWorks.step2.description')}
                </p>
              </div>

              <div
                id="step-2"
                className={`transition-all duration-700 lg:min-h-[60vh] flex flex-col justify-center ${activeStep === 2 ? 'opacity-100 scale-100' : 'opacity-40 lg:opacity-20 scale-95 lg:scale-95 blur-0 lg:blur-sm'}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-white/10 to-white/0 border border-white/10 rounded-2xl flex items-center justify-center shadow-xl backdrop-blur-md mb-6 transition-colors duration-500">
                  <Heart className={`w-8 h-8 ${activeStep === 2 ? 'text-orange-500' : 'text-gray-500'}`} />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold mb-6">{t('howItWorks.step3.title')}</h3>
                <p className="text-xl text-gray-400 leading-relaxed font-medium">
                  {t('howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="download" className="py-24 px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="relative rounded-[3rem] p-12 lg:p-20 bg-gradient-to-br from-waffle-orange/20 to-orange-600/10 border border-waffle-orange/20 shadow-[0_0_100px_-20px_rgba(249,115,22,0.3)] text-center overflow-hidden backdrop-blur-xl">
            <div className="absolute inset-0 bg-[url('/noise.png')] opacity-10 mix-blend-overlay"></div>

            <h2 className="text-4xl lg:text-5xl font-bold mb-6 relative z-10">{t('finalCTA.heading')}</h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto relative z-10">
              {t('finalCTA.subheading')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
              <a href="#" className="px-8 py-4 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-3">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                {t('finalCTA.appStore')}
              </a>
              <a href="#" className="px-8 py-4 bg-gray-900 border border-white/20 text-white font-semibold rounded-full hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-3">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                {t('finalCTA.googlePlay')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-12 px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-gray-400 text-sm">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Logo" className="w-6 h-6 opacity-50 grayscale" />
            <span>{tc('footer.copyright', { year: new Date().getFullYear() })}</span>
          </div>
          <div className="flex gap-6">
            <a href={localizedPath('/privacy')} className="hover:text-white transition-colors">{tc('footer.privacy')}</a>
            <a href={localizedPath('/terms')} className="hover:text-white transition-colors">{tc('footer.terms')}</a>
            <a href="mailto:hello@familywaffle.com" className="hover:text-white transition-colors">{tc('footer.contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
